import {
  createRootRouteWithContext,
  createRoute,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { z } from 'zod';

import AppLayout from '@/components/blocks/layout/app-layout';
import Centered from '@/components/blocks/layout/centered';
import type { AuthContextProps } from '@/providers/auth-provider';
import ClusterReview from '@/routes/(app)/(clusters)/cluster-review';
import Clusters from '@/routes/(app)/(clusters)/clusters';
import Customers from '@/routes/(app)/(customers)/customers';
import ShelfDetail from '@/routes/(app)/(customers)/shelf-detail';
import Shelves from '@/routes/(app)/(customers)/shelves';
import Submissions from '@/routes/(app)/(customers)/submissions';
import { decodeUsernameFromToken, getToken } from '@/routes/(auth)/lib/token';
import Login from '@/routes/(auth)/login';
import Root from '@/routes/root';

interface RouterContext {
  auth?: AuthContextProps;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Root,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: ({ context }) => {
    if (context.auth?.isAuthenticated) {
      throw redirect({ to: '/app/shelves' });
    } else {
      throw redirect({ to: '/auth/login' });
    }
  },
});

const authRoute = createRoute({
  path: 'auth',
  getParentRoute: () => rootRoute,
  component: () => (
    <Centered>
      <Outlet />
    </Centered>
  ),
});

const loginRoute = createRoute({
  getParentRoute: () => authRoute,
  path: 'login',
  validateSearch: z.object({
    redirect: z.string().optional(),
    token: z.string().optional(),
  }),
  beforeLoad: ({ context, search }) => {
    if (search.token && !context.auth?.isAuthenticated) {
      context.auth?.login({
        access_token: search.token,
        username: decodeUsernameFromToken(search.token),
      });
    }
    if (search.token || context.auth?.isAuthenticated) {
      throw redirect({
        to: decodeURIComponent(
          search.redirect ?? encodeURIComponent('/app/shelves')
        ),
        replace: true,
      });
    }
  },
  component: Login,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'app',
  beforeLoad: ({ context, location }) => {
    if (!context.auth?.isAuthenticated && !getToken()) {
      throw redirect({
        to: '/auth/login',
        search: {
          redirect: encodeURIComponent(location.href),
        },
      });
    }
  },
  component: () => (
    <AppLayout>
      <Outlet />
    </AppLayout>
  ),
});

const customersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'shelves',
  component: Customers,
});

const submissionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'shelves/$customerId',
  component: Submissions,
});

const submissionDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'shelves/$customerId/$submissionId',
  component: Shelves,
});

const shelfDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'shelves/$customerId/$submissionId/$shelveId',
  component: ShelfDetail,
});

// The list filters travel to the review page so Done can pick the next
// cluster from the same list and Back returns to it.
const clusterFiltersSchema = z.object({
  category: z.array(z.string()).optional().catch(undefined),
  showFinished: z.boolean().optional().catch(undefined),
  page: z.number().int().min(0).optional().catch(undefined),
});

const clustersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'clusters',
  validateSearch: clusterFiltersSchema,
  component: Clusters,
});

const clusterReviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'clusters/$clusterId',
  validateSearch: clusterFiltersSchema,
  component: ClusterReview,
});

export const routes = rootRoute.addChildren([
  indexRoute,
  authRoute.addChildren([loginRoute]),
  appRoute.addChildren([
    customersRoute,
    submissionsRoute,
    submissionDetailRoute,
    shelfDetailRoute,
    clustersRoute,
    clusterReviewRoute,
  ]),
]);
